import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import {
  type DataProvider,
  useTranslate,
  useNotify,
  useRefresh,
  useDataProvider,
  Form,
  useGetList,
  Confirm,
} from 'react-admin';
import { useState, useEffect, useMemo } from 'react';
import {
  useFormContext,
  useWatch,
  useFormState,
  type FieldValues,
} from 'react-hook-form';
import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Box,
} from '@mui/material';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import moment from 'moment';
import {
  type AssigneeChoice,
  buildAssigneeChoices,
  buildInitialCertificationsByAssetId,
  getGroupIdsWithAppUsers,
  userCertificationTransformer,
  type ExistingCertifiedUser,
} from '../../../utils/certifiedUsersAssignments';
import { notifyWithError } from '../../../utils/errorHandling';
import { hasCertificationDocument, type DocumentAssignee } from '../../../utils/certificationDocuments';
import {
  type CertificationUserDetails,
  type CertificationsByAssetId,
} from '../../../utils/certificationUserForm';
import { CertificationUserTable } from './CertificationUserTable';
import { BhvkCancelButton, BhvkDialogConfirmButton } from '../buttons/ActionButtons';
import { AddCertifiedUserPicker } from './inputs/AddCertifiedUserPicker';

export type CertifiedUsersCategory = {
  id?: string;
  name: string;
  validityPeriod: ExpiryTimeGranularity;
  isDocumentaryProofRequired: boolean;
};

export type CertifiedUsersFormValue = {
  certificationsByAssetId: CertificationsByAssetId;
  __addUserPicker: null;
};

export type CertifiedUsersDraft = {
  recipients: DocumentAssignee[];
  certificationsByAssetId: CertificationsByAssetId;
  newRecipientIds: string[];
};

type AddCertifiedUsersModalProps = {
  open: boolean;
  onClose: () => void;
  categories: CertifiedUsersCategory[];
  onComplete?: () => void;
  onCreateCategories?: () => Promise<CertifiedUsersCategory[]>;
  allowEmptyConfirm?: boolean;
  choices?: AssigneeChoice[];
  existingUsers?: ExistingCertifiedUser[];
  isChoicesLoading?: boolean;
  onBack?: () => void;
  onContinue?: (draft: CertifiedUsersDraft) => void;
  onStepLeave?: (draft: CertifiedUsersDraft) => void;
  initialDraft?: CertifiedUsersDraft | null;
  formInstanceKey?: number;
};

const certificationDetailsChanged = (
  initial: CertificationUserDetails | undefined,
  current: CertificationUserDetails | undefined,
): boolean => {

  if (!initial || !current) return Boolean(current);

  const initialHasDocument = Boolean(initial.document?.rawFile || initial.document?.src);
  const currentHasDocument = Boolean(current.document?.rawFile || current.document?.src);

  return (
    initial.firstIssuedOn !== current.firstIssuedOn
    || initial.lastReceivedOn !== current.lastReceivedOn
    || initial.expiryDate !== current.expiryDate
    || Boolean(current.document?.rawFile)
    || initialHasDocument !== currentHasDocument
  );

};

const createUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  categoryId: string,
  details: CertificationUserDetails,
) => dataProvider.create('user-certifications', {
  data: userCertificationTransformer({
    certificationCategory: categoryId,
    firstIssuedOn: details.firstIssuedOn,
    lastReceivedOn: details.lastReceivedOn,
    expiryDate: details.expiryDate,
    plannedRenewalDate: null,
    ...(details.document?.rawFile ? { document: details.document } : {}),
  }),
  meta: { assetId },
});

const deleteUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  certificationId: string,
) => dataProvider.delete('user-certifications', {
  id: certificationId,
  previousData: { id: certificationId },
  meta: { assetId },
});

const updateUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  certificationId: string,
  details: CertificationUserDetails,
  previousData: ExistingCertifiedUser,
) => dataProvider.update('user-certifications', {
  id: certificationId,
  data: {
    ...(details.firstIssuedOn
      ? { firstIssuedOn: new Date(details.firstIssuedOn).toISOString() }
      : {}),
    lastReceivedOn: new Date(details.lastReceivedOn).toISOString(),
    expiryDate: new Date(details.expiryDate).toISOString(),
    ...(details.document?.rawFile ? { document: details.document } : {}),
  },
  previousData,
  meta: { assetId, certificationId },
});

const buildCertifiedUsersDraft = (
  formValues: CertifiedUsersFormValue,
  recipients: DocumentAssignee[],
  newRecipientIds: Set<string>,
): CertifiedUsersDraft => ({
  recipients,
  certificationsByAssetId: formValues.certificationsByAssetId,
  newRecipientIds: Array.from(newRecipientIds),
});

const hasModalChanges = (
  recipients: DocumentAssignee[],
  initialRecipients: DocumentAssignee[],
  initialCertifications: CertificationsByAssetId,
  currentCertifications: CertificationsByAssetId | undefined,
): boolean => {

  const currentIds = new Set(recipients.map((r) => r.id));
  const initialIds = new Set(initialRecipients.map((r) => r.id));

  if (currentIds.size !== initialIds.size) return true;

  for (const id of currentIds) {

    if (!initialIds.has(id)) return true;

  }

  return recipients.some((recipient) => certificationDetailsChanged(
    initialCertifications[recipient.id],
    currentCertifications?.[recipient.id],
  ));

};

const CertifiedUsersConfirmButton = (props: {
  recipients: DocumentAssignee[];
  initialRecipients: DocumentAssignee[];
  initialCertifications: CertificationsByAssetId;
  requiresDocumentaryProof: boolean;
  isSubmitting: boolean;
  allowEmptyConfirm?: boolean;
  label?: string;
}) => {

  const translate = useTranslate();
  const certificationsByAssetId = useWatch({
    name: 'certificationsByAssetId',
  }) as CertificationsByAssetId | undefined;

  const documentsReady = !props.requiresDocumentaryProof
    || props.recipients.every((recipient) => (
      hasCertificationDocument(certificationsByAssetId?.[recipient.id]?.document)
    ));

  const hasChanges = hasModalChanges(
    props.recipients,
    props.initialRecipients,
    props.initialCertifications,
    certificationsByAssetId,
  );

  return (
    <BhvkDialogConfirmButton
      type="submit"
      form="add-certified-users-form"
      disabled={
        props.isSubmitting
        || (!hasChanges && !props.allowEmptyConfirm)
        || !documentsReady
      }
      startIcon={props.isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined}
    >
      {translate(props.label ?? 'ra.action.confirm')}
    </BhvkDialogConfirmButton>
  );

};

const AddCertifiedUsersCloseButton = (props: {
  onCloseClick: (isDirty: boolean) => void;
  disabled: boolean;
}) => {

  const translate = useTranslate();
  const { isDirty } = useFormState();

  return (
    <IconButton
      onClick={() => props.onCloseClick(isDirty)}
      aria-label={translate('general.text.close')}
      disabled={props.disabled}
    >
      <CloseIcon />
    </IconButton>
  );

};

const AddCertifiedUsersCancelButton = (props: {
  onCloseClick: (isDirty: boolean) => void;
  disabled: boolean;
}) => {

  const { isDirty } = useFormState();

  return (
    <BhvkCancelButton
      onClick={() => props.onCloseClick(isDirty)}
      disabled={props.disabled}
    />
  );

};

const CertifiedUsersModalBody = (props: {
  recipients: DocumentAssignee[];
  setRecipients: React.Dispatch<React.SetStateAction<DocumentAssignee[]>>;
  newRecipientIds: Set<string>;
  setNewRecipientIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  choices: AssigneeChoice[];
  isChoicesLoading: boolean;
  isSubmitting: boolean;
  primaryCategory?: CertifiedUsersCategory;
  requiresDocumentaryProof: boolean;
  onRemoveRecipient: (recipient: DocumentAssignee) => void;
  initialCertifications: CertificationsByAssetId;
  initialRecipientIds: Set<string>;
}) => {

  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const tableAssetIds = useMemo(() => new Set(props.recipients.map((r) => r.id)), [props.recipients]);

  const handleAddRecipients = (added: DocumentAssignee[]) => {

    if (added.length === 0 || !props.primaryCategory) return;

    const today = moment().format('YYYY-MM-DD');
    const expiry = moment()
      .add(
        props.primaryCategory.validityPeriod.value,
        props.primaryCategory.validityPeriod.type,
      )
      .format('YYYY-MM-DD');

    const existing = getValues('certificationsByAssetId') as CertificationsByAssetId;

    const nextCertifications: CertificationsByAssetId = { ...existing };

    added.forEach((assignee) => {

      if (nextCertifications[assignee.id]) return;

      if (props.initialCertifications[assignee.id]) {

        nextCertifications[assignee.id] = props.initialCertifications[assignee.id];
        return;

      }

      nextCertifications[assignee.id] = {
        firstIssuedOn: today,
        lastReceivedOn: today,
        expiryDate: expiry,
      };

    });

    setValue('certificationsByAssetId', nextCertifications, { shouldDirty: true });

    props.setRecipients((prev) => [...prev, ...added]);

    props.setNewRecipientIds((prev) => {

      const next = new Set(prev);
      added.forEach((assignee) => {

        if (!props.initialRecipientIds.has(assignee.id)) {

          next.add(assignee.id);

        }

      });
      return next;

    });

  };

  return (
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.certification-categories.text.certified_users_dates_subtitle')}
          </Typography>

          <Box sx={{ minWidth: 280, maxWidth: 360, flexShrink: 0 }}>
            <AddCertifiedUserPicker
              choices={props.choices}
              tableAssetIds={tableAssetIds}
              disabled={props.isChoicesLoading || props.isSubmitting}
              onAdd={handleAddRecipients}
            />
          </Box>
        </Box>

        {props.primaryCategory && (
          <CertificationUserTable
            recipients={props.recipients}
            newRecipientIds={props.newRecipientIds}
            timeGranularity={props.primaryCategory.validityPeriod}
            includeFirstIssuedOn
            requiresDocument={props.requiresDocumentaryProof}
            onRemoveRecipient={props.onRemoveRecipient}
          />
        )}
      </Stack>
    </DialogContent>
  );

};

const CertifiedUsersBackButton = (props: {
  disabled: boolean;
  recipients: DocumentAssignee[];
  newRecipientIds: Set<string>;
  onBack?: () => void;
  onStepLeave?: (draft: CertifiedUsersDraft) => void;
}) => {

  const translate = useTranslate();
  const { getValues } = useFormContext();

  const handleBack = () => {

    if (props.onStepLeave) {

      const formValues = getValues() as CertifiedUsersFormValue;
      props.onStepLeave(
        buildCertifiedUsersDraft(formValues, props.recipients, props.newRecipientIds),
      );

    }
    props.onBack?.();

  };

  return (
    <IconButton
      onClick={handleBack}
      aria-label={translate('ra.action.back')}
      disabled={props.disabled}
    >
      <ArrowBackIcon />
    </IconButton>
  );

};

export const AddCertifiedUsersModal = (props: AddCertifiedUsersModalProps) => {

  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const allowEmptyConfirm = Boolean(props.onCreateCategories) || props.allowEmptyConfirm;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validGroupIds, setValidGroupIds] = useState<Set<string> | null>(null);
  const [membersCountByGroupId, setMembersCountByGroupId] = useState<Map<string, number>>(new Map());
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const hasExternalChoices = props.choices !== undefined;
  const existingUsers = useMemo(
    () => props.existingUsers ?? [],
    [props.existingUsers],
  );
  const primaryCategory = props.categories[0];
  const requiresDocumentaryProof = props.categories.some((c) => c.isDocumentaryProofRequired);
  const categoryLabel = props.categories.map((c) => c.name).join(', ');

  const initialRecipients = useMemo(
    () => existingUsers
      .map((user) => ({ id: user.assetId, label: user.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [existingUsers],
  );

  const initialCertifications = useMemo(
    () => buildInitialCertificationsByAssetId(existingUsers),
    [existingUsers],
  );

  const [recipients, setRecipients] = useState<DocumentAssignee[]>(initialRecipients);
  const [newRecipientIds, setNewRecipientIds] = useState<Set<string>>(new Set());

  const initialRecipientIds = useMemo(
    () => new Set(initialRecipients.map((r) => r.id)),
    [initialRecipients],
  );

  const { data: users = [], isPending: isUsersPending } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  const { data: groups = [], isPending: isGroupsPending } = useGetList('asset-groups', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  const existingUsersFormKey = useMemo(
    () => existingUsers
      .map((user) => [
        user.assetId,
        user.certificationId,
        user.firstIssuedOn,
        user.lastReceivedOn,
        user.expiryDate,
        user.documentaryProof ?? '',
      ].join(':'))
      .sort()
      .join('|'),
    [existingUsers],
  );

  useEffect(() => {

    if (!props.open) {

      setDiscardConfirmOpen(false);
      return;

    }

    if (props.initialDraft) {

      setRecipients(props.initialDraft.recipients);
      setNewRecipientIds(new Set(props.initialDraft.newRecipientIds));
      return;

    }

    setRecipients(initialRecipients);
    setNewRecipientIds(new Set());

  }, [props.open, initialRecipients, props.initialDraft]);

  useEffect(() => {

    if (hasExternalChoices) return undefined;

    let cancelled = false;

    if (!props.open) {

      setValidGroupIds(null);
      setMembersCountByGroupId(new Map());

    } else if (groups.length === 0) {

      setValidGroupIds(new Set());
      setMembersCountByGroupId(new Map());

    } else {

      getGroupIdsWithAppUsers(groups).then(({
        validGroupIds: validIds,
        membersCountByGroupId: membersCountById,
      }) => {

        if (!cancelled) {

          setValidGroupIds(validIds);
          setMembersCountByGroupId(membersCountById);

        }

      });

    }

    return () => {

      cancelled = true;

    };

  }, [props.open, groups, hasExternalChoices]);

  const baseChoices = useMemo(() => {

    if (hasExternalChoices) return [];

    if (validGroupIds === null) {

      return buildAssigneeChoices(users, []);

    }

    return buildAssigneeChoices(
      users,
      groups.filter((group) => validGroupIds.has(String(group.id))),
      membersCountByGroupId,
    );

  }, [users, groups, validGroupIds, membersCountByGroupId, hasExternalChoices]);

  const choices = hasExternalChoices ? (props.choices ?? []) : baseChoices;

  const isInternalLoading = !hasExternalChoices && (
    isUsersPending || isGroupsPending || (groups.length > 0 && validGroupIds === null)
  );
  const isChoicesLoading = props.isChoicesLoading ?? isInternalLoading;

  const defaultValues: CertifiedUsersFormValue = {
    certificationsByAssetId:
      props.initialDraft?.certificationsByAssetId ?? initialCertifications,
    __addUserPicker: null,
  };

  const requestClose = (isDirty: boolean) => {

    if (isDirty) {

      setDiscardConfirmOpen(true);
      return;

    }

    props.onClose();

  };

  const confirmDiscard = () => {

    setDiscardConfirmOpen(false);
    props.onClose();

  };

  const handleRemoveRecipient = (recipient: DocumentAssignee) => {

    setRecipients((prev) => prev.filter((r) => r.id !== recipient.id));
    setNewRecipientIds((prev) => {

      const next = new Set(prev);
      next.delete(recipient.id);
      return next;

    });

  };

  const handleSubmit = async (values: FieldValues) => {

    const formValues = values as CertifiedUsersFormValue;

    if (props.onContinue) {

      props.onContinue(
        buildCertifiedUsersDraft(formValues, recipients, newRecipientIds),
      );
      return;

    }

    setIsSubmitting(true);

    try {

      const currentIds = new Set(recipients.map((r) => r.id));
      const initialByAssetId = new Map(existingUsers.map((u) => [u.assetId, u]));

      const toRemove = existingUsers.filter((u) => !currentIds.has(u.assetId));
      const toCreate = recipients.filter(
        (r) => newRecipientIds.has(r.id) && !initialByAssetId.has(r.id),
      );
      const toUpdate = recipients.filter((r) => {

        const existing = initialByAssetId.get(r.id);

        if (!existing) return false;

        return certificationDetailsChanged(
          initialCertifications[r.id],
          formValues.certificationsByAssetId[r.id],
        );

      });

      let categories = props.categories;

      if (props.onCreateCategories) {

        categories = await props.onCreateCategories();

      }

      if (toCreate.length === 0 && toUpdate.length === 0 && toRemove.length === 0) {

        notify(translate('resources.certification-categories.text.no_users_added'), { type: 'success' });
        refresh();
        props.onComplete?.();
        if (!props.onComplete) props.onClose();
        return;

      }

      await Promise.all([
        ...categories.flatMap((category) => {

          const categoryId = category.id;
          if (!categoryId) return [];

          return toCreate.map((recipient) => {

            const details = formValues.certificationsByAssetId[recipient.id];
            if (!details) return Promise.resolve();
            return createUserCertification(dataProvider, recipient.id, categoryId, details);

          });

        }),
        ...toUpdate.flatMap((recipient) => {

          const existing = initialByAssetId.get(recipient.id);
          const details = formValues.certificationsByAssetId[recipient.id];

          if (!existing || !details) return [];

          return [updateUserCertification(
            dataProvider,
            recipient.id,
            existing.certificationId,
            details,
            existing,
          )];

        }),
        ...toRemove.map((u) => deleteUserCertification(dataProvider, u.assetId, u.certificationId)),
      ]);

      const parts: string[] = [];

      if (toCreate.length > 0) {

        parts.push(
          translate('resources.certification-categories.text.users_added', {
            smart_count: toCreate.length,
          }),
        );

      }

      if (toUpdate.length > 0) {

        parts.push(
          translate('resources.certification-categories.text.users_updated', {
            smart_count: toUpdate.length,
          }),
        );

      }

      if (toRemove.length > 0) {

        parts.push(
          translate('resources.certification-categories.text.users_removed', {
            smart_count: toRemove.length,
          }),
        );

      }

      if (parts.length > 0) {

        notify(parts.join(', '), { type: 'success' });

      }

      refresh();
      props.onComplete?.();
      if (!props.onComplete) props.onClose();

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      setIsSubmitting(false);

    }

  };

  return (
    <>
      <Dialog
        open={props.open}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': { overflowY: 'hidden' },
          '& form': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
          },
        }}
      >
        <Form
          id="add-certified-users-form"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          key={
            props.open
              ? `certified-users-${props.formInstanceKey ?? 0}-${existingUsersFormKey}`
              : 'closed'
          }
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {props.onBack && (
                <CertifiedUsersBackButton
                  disabled={isSubmitting}
                  recipients={recipients}
                  newRecipientIds={newRecipientIds}
                  onBack={props.onBack}
                  onStepLeave={props.onStepLeave}
                />
              )}
              <Typography variant="body1" fontSize="20px">
                {translate('resources.certification-categories.text.certified_users_modal_title', {
                  category: categoryLabel,
                })}
              </Typography>
            </Stack>
            <AddCertifiedUsersCloseButton onCloseClick={requestClose} disabled={isSubmitting} />
          </DialogTitle>

          <CertifiedUsersModalBody
            initialCertifications={initialCertifications}
            initialRecipientIds={initialRecipientIds}
            recipients={recipients}
            setRecipients={setRecipients}
            newRecipientIds={newRecipientIds}
            setNewRecipientIds={setNewRecipientIds}
            choices={choices}
            isChoicesLoading={isChoicesLoading}
            isSubmitting={isSubmitting}
            primaryCategory={primaryCategory}
            requiresDocumentaryProof={requiresDocumentaryProof}
            onRemoveRecipient={handleRemoveRecipient}
          />

          <DialogActions>
            <AddCertifiedUsersCancelButton
              onCloseClick={requestClose}
              disabled={isSubmitting}
            />
            <CertifiedUsersConfirmButton
              label={props.onContinue ? 'general.text.next' : undefined}
              allowEmptyConfirm={allowEmptyConfirm}
              recipients={recipients}
              initialRecipients={initialRecipients}
              initialCertifications={initialCertifications}
              requiresDocumentaryProof={requiresDocumentaryProof}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Form>
      </Dialog>

      <Confirm
        isOpen={discardConfirmOpen}
        title="resources.certification-categories.text.discard_changes_title"
        content="resources.certification-categories.text.discard_changes_content"
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>
  );

};
